/**
 * Tenant-management-klient för Lyktan (S-04-33 + S-04-34 + S-04-36).
 *
 * Wrappar superadmin-endpoints från S-04-31:
 *  - `GET   /api/admin/tenants`                  — lista alla tenants
 *  - `GET   /api/admin/tenants/{id}`             — hämta enskild tenant
 *  - `PATCH /api/admin/tenants/{id}`             — uppdatera guest_policy + is_active
 *  - `GET   /api/admin/tenants/{id}/tier-stats`  — tier-counts (S-04-36)
 *
 * Tillagt i S-04-34: `updateTenant()` för Edit-modal. Backend kräver
 * MFA-step-up + CSRF-header för PATCH (en stulen cookie räcker inte för
 * att ändra tenant-konfig).
 *
 * Tillagt i S-04-36: `getTenantTierStats()` + k=5-skydd. Backend
 * returnerar `-1` för buckets med 1..4 användare; UI renderar `<5` med
 * varningsfärg så superadmin ser att antalet är k=5-maskerat.
 *
 * Hard-rules som tillämpas:
 *  - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i body
 *    eller localStorage/sessionStorage.
 *  - CSRF-header på alla muterande anrop (PATCH) — speglar
 *    `harden_admin_csrf`-cookien (S-12-11c). GET-listning är inte
 *    CSRF-skyddad på backend (SameSite=Strict räcker för GET).
 *  - Backend returnerar UUID som tenant-id — inga råa databas-ID:n.
 *  - Backend ger även 403 för admin utan superadmin-flagga och 401 utan
 *    auth — UI gating sker via `isSuperadmin`-flaggan från `/api/me`,
 *    server-side gating via `requireSuperadmin` är fortfarande
 *    auktoritativ källa.
 *  - `guest_policy='open'` returnerar 422 `open_requires_attestation` i
 *    MVP1 (Tier 1-attestation saknas). UI mappar till svensk error-toast.
 *  - Felmeddelanden mappas till svenska användartexter; backend-felkoderna
 *    är opaka tokens och loggas inte vidare.
 */

import { csrfHeader } from '../auth/csrf';

/**
 * Tenant-rad i listan. Återspeglar [AdminTenantItem] i backend
 * (`AdminTenantRoutes.kt`).
 */
export type Tenant = {
  /** Opaque tenant-UUID. Råa databas-ID:n läcker aldrig. */
  id: string;
  /** Tenant-namn (mänskligt läsbart, t.ex. "Hisings Backa"). */
  name: string;
  /** Slug (3-40 lowercase alphanumeric/hyphen, t.ex. "hisings-backa"). */
  slug: string;
  /** Wire-värde "open" | "invitation_only" | "hybrid". */
  guestPolicy: GuestPolicy;
  /** false = soft-deactiverad. Backend bevarar raden + audit-trail. */
  isActive: boolean;
  /** ISO-8601 timestamp för när tenant skapades. */
  createdAt: string;
};

/**
 * Wire-värden för `guest_policy` enligt S-02-23 + S-04-31.
 * UI mappar till svensk etikett separat (t.ex. "Endast inbjudan").
 */
export type GuestPolicy = 'open' | 'invitation_only' | 'hybrid';

export type TenantListResult =
  | { kind: 'ok'; items: Tenant[] }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

export type TenantGetResult =
  | { kind: 'ok'; tenant: Tenant }
  | { kind: 'unauthorized' }
  | { kind: 'not_found' }
  | { kind: 'failure'; message: string };

/**
 * Resultat av PATCH /api/admin/tenants/{id} (S-04-34).
 *
 * Modal-vyn skiljer på typade kind-värden (för UI-flow) och `failure`
 * (generisk svensk text för okända fel). `open_requires_attestation`
 * är ett dedikerat kind eftersom UI-vyn vill markera radio-knappen
 * "Öppen" som invalid och ge en åtgärdsinstruktion (välj annan policy).
 */
export type TenantUpdateResult =
  | { kind: 'ok'; tenant: Tenant }
  | { kind: 'unauthorized' }
  | { kind: 'not_found' }
  | { kind: 'mfa_not_configured' }
  | { kind: 'mfa_locked' }
  | { kind: 'invalid_mfa_code' }
  | { kind: 'open_requires_attestation' }
  | { kind: 'invalid_guest_policy' }
  | { kind: 'failure'; message: string };

/** Patch-payload för [updateTenant]. Båda fälten är obligatoriska. */
export type TenantUpdatePatch = {
  guestPolicy: GuestPolicy;
  isActive: boolean;
};

const API_BASE = '/api/admin/tenants';

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera tenants (kräver Compuna-superadmin).';
    case 'invalid_request':
      return 'Ogiltig begäran. Kontrollera fälten och försök igen.';
    case 'not_found':
      return 'Tenant kunde inte hittas.';
    case 'invalid_mfa_code':
      return 'Felaktig MFA-kod. Försök igen.';
    case 'mfa_locked':
      return 'För många misslyckade MFA-försök. Vänta en stund och försök igen.';
    case 'mfa_not_configured':
      return 'Du måste aktivera tvåfaktor först. Gå till Konto → Aktivera tvåfaktor.';
    case 'invalid_guest_policy':
      return 'Ogiltig gäst-policy. Välj öppen, endast inbjudan eller hybrid.';
    case 'open_requires_attestation':
      return 'Öppen gäst-policy kräver att enhets-attestation är aktiv (saknas i MVP1). Välj endast inbjudan eller hybrid.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

type RawTenant = {
  id?: unknown;
  name?: unknown;
  slug?: unknown;
  guestPolicy?: unknown;
  isActive?: unknown;
  createdAt?: unknown;
};

type RawListResponse = {
  items?: unknown;
};

function parseGuestPolicy(value: unknown): GuestPolicy | null {
  if (value === 'open' || value === 'invitation_only' || value === 'hybrid') {
    return value;
  }
  return null;
}

function parseTenant(raw: RawTenant): Tenant | null {
  if (
    typeof raw.id !== 'string' ||
    typeof raw.name !== 'string' ||
    typeof raw.slug !== 'string' ||
    typeof raw.isActive !== 'boolean' ||
    typeof raw.createdAt !== 'string'
  ) {
    return null;
  }
  const guestPolicy = parseGuestPolicy(raw.guestPolicy);
  if (guestPolicy === null) {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    guestPolicy,
    isActive: raw.isActive,
    createdAt: raw.createdAt,
  };
}

/**
 * GET /api/admin/tenants — listar alla tenants i name-ordning.
 *
 * Backend returnerar både aktiva och soft-deaktiverade tenants — UI:t
 * särskiljer dem visuellt via Status-kolumnen ("Aktiv" / "Inaktiv").
 */
export async function listTenants(): Promise<TenantListResult> {
  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let body: RawListResponse;
  try {
    body = (await response.json()) as RawListResponse;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (!Array.isArray(body.items)) {
    return { kind: 'failure', message: messageForError('default') };
  }

  const items: Tenant[] = [];
  for (const raw of body.items) {
    const parsed = parseTenant(raw as RawTenant);
    if (!parsed) {
      return { kind: 'failure', message: messageForError('default') };
    }
    items.push(parsed);
  }
  return { kind: 'ok', items };
}

/**
 * GET /api/admin/tenants/{id} — hämtar enskild tenant.
 *
 * Klient-användning: Edit-modal (S-04-34) hämtar färska värden innan
 * den öppnas så att vyn aldrig redigerar stale data. I MVP1 (read-only)
 * är funktionen exponerad för testbarhet och framtida wire-up — knappen
 * "Redigera" är disabled idag.
 */
export async function getTenant(id: string): Promise<TenantGetResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (response.status === 400) {
    // Ogiltig UUID — backend returnerar 400 invalid_request.
    return { kind: 'failure', message: messageForError('invalid_request') };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let raw: RawTenant;
  try {
    raw = (await response.json()) as RawTenant;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  const parsed = parseTenant(raw);
  if (!parsed) {
    return { kind: 'failure', message: messageForError('default') };
  }
  return { kind: 'ok', tenant: parsed };
}

/**
 * PATCH /api/admin/tenants/{id} — uppdatera guest_policy + is_active (S-04-34).
 *
 * Backend kräver MFA-step-up (TOTP-kod) från aktören och CSRF-header.
 * Klienten skickar koden separat — den ska aldrig hamna i URL:en eller
 * loggas vidare. Returnerar uppdaterad tenant så UI:t slipper en extra
 * GET-rundtur.
 *
 * Felhantering följer admin-mönster (`adminUsersClient`):
 *  - 401 + `invalid_mfa_code` → `invalid_mfa_code` (visa MFA-fel inline)
 *  - 401 utan kod → `unauthorized` (sessionen ut, tvinga logout-flow)
 *  - 403 → `unauthorized` (saknar superadmin-flagga)
 *  - 404 → `not_found` (tenant raderad mellan list och PATCH)
 *  - 409 + `mfa_not_configured` → `mfa_not_configured` (Settings-länk)
 *  - 422 + `open_requires_attestation` → dedikerat kind (UI markerar
 *    "Öppen"-radio som invalid med svensk åtgärdsinstruktion)
 *  - 422 + `invalid_guest_policy` → dedikerat kind (UI ber om val)
 *  - 429 → `mfa_locked`
 *  - Annat → `failure` med svensk text
 */
export async function updateTenant(
  id: string,
  patch: TenantUpdatePatch,
  mfaCode: string,
): Promise<TenantUpdateResult> {
  const body = JSON.stringify({
    guestPolicy: patch.guestPolicy,
    isActive: patch.isActive,
    mfaCode: mfaCode,
  });

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body,
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 400) {
    const errorBody = await safeJson(response);
    // Backend returnerar 400 invalid_request både för "MFA-kod krävs"
    // (tom kod) och för "Ogiltigt tenant-id". Båda är svenska felmeddelanden
    // för användaren — vi mappar till generisk failure.
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'invalid_request') };
  }
  if (response.status === 401) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'invalid_mfa_code') return { kind: 'invalid_mfa_code' };
    return { kind: 'unauthorized' };
  }
  if (response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (response.status === 409) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'mfa_not_configured') return { kind: 'mfa_not_configured' };
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }
  if (response.status === 422) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'open_requires_attestation') {
      return { kind: 'open_requires_attestation' };
    }
    if (errorBody?.error === 'invalid_guest_policy') {
      return { kind: 'invalid_guest_policy' };
    }
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'invalid_request') };
  }
  if (response.status === 429) {
    return { kind: 'mfa_locked' };
  }
  if (!response.ok) {
    const errorBody = await safeJson(response);
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }

  let raw: RawTenant;
  try {
    raw = (await response.json()) as RawTenant;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  const parsed = parseTenant(raw);
  if (!parsed) {
    return { kind: 'failure', message: messageForError('default') };
  }
  return { kind: 'ok', tenant: parsed };
}

// ===================================================================
// S-04-36 — Tier-statistik per tenant
// ===================================================================

/**
 * Tier-counts som backend returnerar. Värden är `>= 0` (faktiska tal)
 * eller `-1` (k=5-skyddat — backend maskerar buckets med 1..4
 * användare). UI renderar `-1` som "<5" med varningsfärg så superadmin
 * ser skillnaden mellan exakt och maskerat värde.
 *
 * Tier-mapping (CLAUDE.md "Onboarding och identitet"):
 *  - tier0 = pre-provisioned (pilot-konton, default post-pilot)
 *  - tier1 = device-tokens (Tier 1-gäster)
 *  - tier2 = SMS-verifierad (alltid 0 i MVP1, SMS-flöde kommer senare)
 *  - tier3 = Freja-verifierad (on-demand vid LOK/juridiska samtycken)
 */
export type TierCounts = {
  tier0: number;
  tier1: number;
  tier2: number;
  tier3: number;
};

export type TenantTierStatsResult =
  | { kind: 'ok'; tenantId: string; counts: TierCounts }
  | { kind: 'unauthorized' }
  | { kind: 'not_found' }
  | { kind: 'failure'; message: string };

/** Sentinel-värde som backend returnerar när k=5-skydd aktiveras. */
export const TIER_COUNT_SUPPRESSED = -1;

function isFiniteInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value);
}

type RawTierStatsResponse = {
  tenantId?: unknown;
  counts?: unknown;
};

function parseTierCounts(raw: unknown): TierCounts | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const c = raw as { tier0?: unknown; tier1?: unknown; tier2?: unknown; tier3?: unknown };
  if (
    !isFiniteInteger(c.tier0) ||
    !isFiniteInteger(c.tier1) ||
    !isFiniteInteger(c.tier2) ||
    !isFiniteInteger(c.tier3)
  ) {
    return null;
  }
  // Värdet får vara `-1` (k=5-suppressed) eller `>= 0`. Annat är
  // protokollbrott — vi vägrar rendera och returnerar failure.
  for (const v of [c.tier0, c.tier1, c.tier2, c.tier3]) {
    if (v !== TIER_COUNT_SUPPRESSED && v < 0) {
      return null;
    }
  }
  return { tier0: c.tier0, tier1: c.tier1, tier2: c.tier2, tier3: c.tier3 };
}

/**
 * GET /api/admin/tenants/{id}/tier-stats — antal användare per tier.
 *
 * Backend appliceran k=5-skydd: buckets med 1..4 användare returneras
 * som -1. Värde 0 lämnas orört (frånvaro avslöjar ingen individ).
 * Värden >= 5 returneras exakt.
 *
 * Felhantering följer samma mönster som [getTenant]:
 *  - 401/403 → `unauthorized` (UI tvingar logout-flow)
 *  - 404 → `not_found` (tenant raderad mellan list och stats-anrop)
 *  - Annat → `failure` med svensk text
 */
export async function getTenantTierStats(id: string): Promise<TenantTierStatsResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(id)}/tier-stats`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (response.status === 400) {
    return { kind: 'failure', message: messageForError('invalid_request') };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let raw: RawTierStatsResponse;
  try {
    raw = (await response.json()) as RawTierStatsResponse;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (typeof raw.tenantId !== 'string') {
    return { kind: 'failure', message: messageForError('default') };
  }
  const counts = parseTierCounts(raw.counts);
  if (counts === null) {
    return { kind: 'failure', message: messageForError('default') };
  }
  return { kind: 'ok', tenantId: raw.tenantId, counts };
}

/**
 * Formatterar en tier-count för visning. Returnerar `<5` när värdet är
 * k=5-suppressed (-1), annars siffran som sträng.
 *
 * Vi exponerar en helper istället för att inline:a logiken i Svelte-
 * mallen så att tester kan låsa formatet utan DOM-render.
 */
export function formatTierCount(count: number): string {
  return count === TIER_COUNT_SUPPRESSED ? '<5' : String(count);
}

/**
 * Returnerar true om count är k=5-suppressed. UI använder för att lägga
 * varningsfärg på siffran.
 */
export function isTierCountSuppressed(count: number): boolean {
  return count === TIER_COUNT_SUPPRESSED;
}

/**
 * Svensk etikett för guest-policy-värden. Används i tabell + Edit-modal.
 * Konsoliderad här så att alla vyer ser samma svenska text.
 */
export function guestPolicyLabel(policy: GuestPolicy): string {
  switch (policy) {
    case 'open':
      return 'Öppen';
    case 'invitation_only':
      return 'Endast inbjudan';
    case 'hybrid':
      return 'Hybrid';
  }
}

/**
 * Svensk status-etikett. Aktiv tenant tar emot nya konton; inaktiv är
 * soft-deaktiverad (se S-04-31 — ingen DELETE-endpoint finns).
 */
export function tenantStatusLabel(isActive: boolean): string {
  return isActive ? 'Aktiv' : 'Inaktiv';
}

/**
 * Svensk användartext för felkoder. Exporteras så att UI-vyn kan
 * rendera samma meddelande för olika fel-källor.
 */
export function tenantErrorMessage(code: string): string {
  return messageForError(code);
}
